/**
 * "My reports": the signed-in Guardian user's own submitted incident reports
 * with a status, title, category, time and location (plan Track 5 B6,
 * 2026-09-30).
 *
 * Data: GET /guardian/incidents?mine=true (fetchMyGuardianIncidents). The
 * Backend scopes that list to the token's user; this view adds no scoping of
 * its own and never lists anything else. The Backend has no explicit report
 * status field, so the status shown is derived from what it does return
 * (insights present / `processed`) and labelled as analysis progress.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import { fetchMyGuardianIncidents } from '../api/guardianApi';

/**
 * Analysis progress of one report, from the fields the Backend returns.
 * @param {object} event
 * @returns {{ label: string, color: 'success'|'default' }}
 */
export function reportStatus(event) {
  const insights = event?.insights;
  const hasInsights = Boolean(
    insights && typeof insights === 'object' && (insights.summary || insights.risk_level),
  );
  if (hasInsights || String(event?.processed).toLowerCase() === 'true') {
    return { label: 'Insights ready', color: 'success' };
  }
  return { label: 'Received - analysis pending', color: 'default' };
}

/** Local date/time for an ISO timestamp; an unparseable one shows an em dash. */
export function formatReportTime(value) {
  if (typeof value !== 'string' || !value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/** Text only: anything else from the API renders as an em dash. */
function text(value) {
  return typeof value === 'string' && value.trim() ? value : '—';
}

/**
 * @param {object} props
 * @param {string|null} props.authToken - Guardian JWT; signed-out shows a sign-in note.
 */
export default function MyReportsView({ authToken }) {
  const [state, setState] = useState({ status: 'idle', events: [], error: null });
  // Only the latest request may write state (token change, refresh, unmount).
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    if (!authToken) {
      setState({ status: 'idle', events: [], error: null });
      return;
    }
    setState((prev) => ({ ...prev, status: 'loading', error: null }));
    let res;
    try {
      res = await fetchMyGuardianIncidents(authToken);
    } catch (err) {
      if (mine !== seq.current) return;
      setState({ status: 'error', events: [], error: err?.message || 'Failed to load your reports' });
      return;
    }
    if (mine !== seq.current) return;
    const events = res?.data?.events;
    if (!res?.success || !Array.isArray(events)) {
      setState({
        status: 'error',
        events: [],
        error: res?.status === 401
          ? 'Your session expired. Please sign in again.'
          : (res?.message || 'Failed to load your reports'),
      });
      return;
    }
    setState({ status: 'ready', events, error: null });
  }, [authToken]);

  useEffect(() => {
    load();
    return () => { seq.current += 1; };
  }, [load]);

  const { status, events, error } = state;

  return (
    <Paper className="il-card" elevation={0} sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">My reports</Typography>
        {authToken && (
          <Button size="small" onClick={load} disabled={status === 'loading'}>
            Refresh
          </Button>
        )}
      </Box>

      {!authToken && (
        <Alert severity="info">Sign in to see the reports you have submitted.</Alert>
      )}

      {authToken && status === 'loading' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} role="status">
          <CircularProgress size={20} />
          <Typography variant="body2">Loading your reports…</Typography>
        </Box>
      )}

      {authToken && status === 'error' && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {authToken && status === 'ready' && events.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          You have not submitted any reports yet.
        </Typography>
      )}

      {authToken && status === 'ready' && events.length > 0 && (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {events.map((event, i) => {
            const st = reportStatus(event);
            const key = typeof event?.id === 'string' && event.id ? event.id : `report-${i}`;
            return (
              <Paper key={key} component="li" variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {text(event?.title)}
                  </Typography>
                  <Chip label={st.label} size="small" color={st.color} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {text(event?.category)} · {formatReportTime(event?.created_at || event?.date)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Location:</strong> {text(event?.location)}
                </Typography>
              </Paper>
            );
          })}
        </Box>
      )}
    </Paper>
  );
}
